import { useState } from 'react';
import { isPlatformAdmin, useAuth } from '@/shared/auth';
import type { UserResponse } from '@/shared/api';
import { UserReauthDialog } from '../UserReauthDialog';
import { fullName } from '../userStatus';
import { platformRoleState } from './platformRole';
import { UserPlatformRoleCard } from './UserPlatformRoleCard';
import { usePlatformRoleChange, type PlatformRoleChange } from './usePlatformRoleChange';

interface Props {
  user: UserResponse;
  isSelf: boolean;
}

/**
 * Plattformrolle samt Bestätigungsdialog. Erscheint nur in der eigenen Plattform-Sitzung eines
 * Plattform-Admins — in einer Support-Sitzung ist die wirksame Anmeldung die des Kunden-Workspace,
 * und der Server lehnt dort ohnehin ab. Der Dialog wohnt hier statt in `UserDetailDialogs`, weil
 * es ihn nur zusammen mit dieser Karte gibt.
 */
export function UserPlatformRoleSection({ user, isSelf }: Props) {
  const viewer = useAuth().user;
  // Die Art bleibt beim Schließen stehen: sonst wechselte der Dialog während der Ausblendung kurz
  // Titel und Text.
  const [dialog, setDialog] = useState<{ open: boolean; change: PlatformRoleChange }>({
    open: false,
    change: 'grant',
  });
  const close = () => setDialog((current) => ({ ...current, open: false }));
  const mutation = usePlatformRoleChange(close);
  if (!isPlatformAdmin(viewer)) return null;

  const name = fullName(user);
  const revoke = dialog.change === 'revoke';
  return (
    <>
      <UserPlatformRoleCard
        state={platformRoleState(user, isSelf)}
        onGrant={() => setDialog({ open: true, change: 'grant' })}
        onRevoke={() => setDialog({ open: true, change: 'revoke' })}
      />
      <UserReauthDialog
        open={dialog.open}
        onOpenChange={(open) => !open && close()}
        title={revoke ? 'Plattform-Admin entziehen' : 'Zum Plattform-Admin machen'}
        userLabel={name}
        confirmLabel={revoke ? 'Rolle entziehen' : 'Plattform-Admin vergeben'}
        confirmVariant={revoke ? 'destructive' : 'brand'}
        loading={mutation.isPending}
        onSubmit={({ password, code }) =>
          mutation.mutate({ id: user.id, change: dialog.change, password, code })
        }
      >
        {revoke ? (
          <p>
            {name} verliert den Zugang zur Plattform-Verwaltung; eine laufende Support-Sitzung endet
            sofort. Die Rolle im Workspace bleibt.
          </p>
        ) : (
          <p>
            {name} kann danach Kunden-Workspaces anlegen und als Support in jeden Kunden-Workspace
            wechseln. Jeder Wechsel verlangt einen frischen Code aus der eigenen Authenticator-App.
          </p>
        )}
      </UserReauthDialog>
    </>
  );
}
